import { mailApiGet, mailApiPost } from "@/lib/api/client";

export const MAIL_WEB_PUSH_PREF_KEY = "mail_web_push_enabled_v1";

export type MailWebPushStatus =
  | "unsupported"
  | "denied"
  | "default"
  | "granted_off"
  | "subscribed"
  | "not_configured"
  | "error";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    out[i] = raw.charCodeAt(i);
  }
  return out;
}

export function isMailWebPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function readMailWebPushPref(): boolean {
  try {
    return localStorage.getItem(MAIL_WEB_PUSH_PREF_KEY) === "1";
  } catch {
    return false;
  }
}

export function persistMailWebPushPref(enabled: boolean): void {
  try {
    localStorage.setItem(MAIL_WEB_PUSH_PREF_KEY, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function mailWebPushStatusLabel(status: MailWebPushStatus): string {
  if (status === "subscribed") return "Alertes OS hors premier plan";
  if (status === "denied") return "Bloquées par le navigateur";
  if (status === "unsupported") return "Non supportées sur cet appareil";
  if (status === "not_configured") return "Serveur non configuré (VAPID)";
  if (status === "granted_off" || status === "default") return "Désactivées";
  return "Indisponibles";
}

async function fetchMailVapidPublicKey(): Promise<{
  configured: boolean;
  publicKey: string | null;
}> {
  const r = await mailApiGet<{ configured?: boolean; publicKey?: string }>(
    "push/vapid-public.php"
  );
  if (!r.ok) return { configured: false, publicKey: null };
  return {
    configured: Boolean(r.data.configured),
    publicKey: typeof r.data.publicKey === "string" ? r.data.publicKey : null,
  };
}

async function waitForServiceWorkerReady(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

export async function getMailWebPushStatus(): Promise<MailWebPushStatus> {
  if (!isMailWebPushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  if (!readMailWebPushPref()) {
    return Notification.permission === "granted" ? "granted_off" : "default";
  }
  const vapid = await fetchMailVapidPublicKey();
  if (!vapid.configured || !vapid.publicKey) return "not_configured";
  const reg = await waitForServiceWorkerReady();
  if (!reg) return "error";
  try {
    const sub = await reg.pushManager.getSubscription();
    return sub ? "subscribed" : "granted_off";
  } catch {
    return "error";
  }
}

export async function enableMailWebPush(): Promise<{
  ok: boolean;
  status: MailWebPushStatus;
  message?: string;
}> {
  if (!isMailWebPushSupported()) {
    return { ok: false, status: "unsupported", message: "Navigateur non compatible." };
  }

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
  if (permission !== "granted") {
    persistMailWebPushPref(false);
    return {
      ok: false,
      status: permission === "denied" ? "denied" : "default",
      message: "Permission notifications refusée.",
    };
  }

  const vapid = await fetchMailVapidPublicKey();
  if (!vapid.configured || !vapid.publicKey) {
    return {
      ok: false,
      status: "not_configured",
      message: "Web Push non configuré côté serveur (VAPID).",
    };
  }

  const reg = await waitForServiceWorkerReady();
  if (!reg) {
    return { ok: false, status: "error", message: "Service worker indisponible." };
  }

  try {
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.publicKey) as BufferSource,
      });
    }

    const json = sub.toJSON();
    const endpoint = json.endpoint;
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;
    if (!endpoint || !p256dh || !auth) {
      return { ok: false, status: "error", message: "Abonnement Push incomplet." };
    }

    const save = await mailApiPost("push/subscribe.php", {
      endpoint,
      keys: { p256dh, auth },
    });
    if (!save.ok) {
      return {
        ok: false,
        status: "error",
        message: save.message || "Enregistrement abonnement impossible.",
      };
    }

    persistMailWebPushPref(true);
    return { ok: true, status: "subscribed" };
  } catch (e) {
    return {
      ok: false,
      status: "error",
      message: e instanceof Error ? e.message : "Abonnement impossible.",
    };
  }
}

export async function disableMailWebPush(): Promise<{
  ok: boolean;
  status: MailWebPushStatus;
  message?: string;
}> {
  persistMailWebPushPref(false);
  if (!isMailWebPushSupported()) {
    return { ok: true, status: "unsupported" };
  }
  try {
    const reg = await waitForServiceWorkerReady();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await mailApiPost("push/unsubscribe.php", { endpoint: sub.endpoint });
      try {
        await sub.unsubscribe();
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* ignore */
  }
  return {
    ok: true,
    status: Notification.permission === "denied" ? "denied" : "granted_off",
  };
}
